/**
 * Global numeral-system store + pure presentation formatters.
 * Internal/API values stay Latin digits; only display output changes.
 */

export const NUMERAL_FA = "fa";
export const NUMERAL_EN = "en";
export const NUMERAL_STORAGE_KEY = "logimind.numeralSystem";

const FA_DIGITS = "۰۱۲۳۴۵۶۷۸۹";

let numeralSystem = NUMERAL_FA;
const listeners = new Set();

function emit() {
  listeners.forEach((listener) => listener());
}

export function getNumeralSystem() {
  return numeralSystem;
}

export function setNumeralSystem(next) {
  const value = next === NUMERAL_EN ? NUMERAL_EN : NUMERAL_FA;
  if (value === numeralSystem) return;
  numeralSystem = value;
  try {
    localStorage.setItem(NUMERAL_STORAGE_KEY, value);
  } catch (_) {
    /* ignore */
  }
  emit();
}

export function readStoredNumeralSystem() {
  try {
    const stored = localStorage.getItem(NUMERAL_STORAGE_KEY);
    if (stored === NUMERAL_EN || stored === NUMERAL_FA) return stored;
  } catch (_) {
    /* ignore */
  }
  return NUMERAL_FA;
}

export function initNumeralSystem(value = readStoredNumeralSystem()) {
  numeralSystem = value === NUMERAL_EN ? NUMERAL_EN : NUMERAL_FA;
}

export function subscribeNumeralSystem(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Intl locale for plain numbers */
export function getNumberLocaleTag(system = numeralSystem) {
  return system === NUMERAL_EN ? "en-US" : "fa-IR";
}

/** Intl locale for dates (Persian calendar, numerals follow setting) */
export function getDateLocaleTag(system = numeralSystem) {
  return system === NUMERAL_EN ? "fa-IR-u-nu-latn" : "fa-IR";
}

/** Convert Latin digits in a string to the active numeral system (display only). */
export function localizeDigits(value, system = numeralSystem) {
  const str = String(value ?? "");
  if (system === NUMERAL_EN) {
    return str
      .replace(/[۰-۹]/g, (d) => String(FA_DIGITS.indexOf(d)))
      .replace(/[٠-٩]/g, (d) => String("٠١٢٣٤٥٦٧٨٩".indexOf(d)));
  }
  return str.replace(/\d/g, (d) => FA_DIGITS[Number(d)]);
}

/**
 * Format a number for display.
 * @param {unknown} value
 * @param {{ maximumFractionDigits?: number, minimumFractionDigits?: number, useGrouping?: boolean, fallback?: string }} [options]
 */
export function formatNumber(value, options = {}) {
  const {
    maximumFractionDigits = 0,
    minimumFractionDigits = 0,
    useGrouping = true,
    fallback = "—",
  } = options;
  if (value == null || value === "") return fallback;
  const n = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(n)) return fallback;
  return n.toLocaleString(getNumberLocaleTag(), {
    maximumFractionDigits,
    minimumFractionDigits,
    useGrouping,
  });
}

export function formatInt(value, fallback = "0") {
  if (value == null || value === "") return localizeDigits(fallback);
  const n = parseInt(value, 10);
  if (Number.isNaN(n)) return localizeDigits(fallback);
  return n.toLocaleString(getNumberLocaleTag());
}

export function formatFixed(value, decimals = 1) {
  const n = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(n)) return localizeDigits("0");
  return localizeDigits(n.toFixed(decimals));
}

export function formatPercent(value, decimals = 1) {
  return `${formatFixed(value, decimals)}%`;
}

export function formatDateTime(value, options = {}) {
  if (value == null || value === "") return "—";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(getDateLocaleTag(), {
    dateStyle: "medium",
    timeStyle: "short",
    ...options,
  });
}

export function formatDate(value, options = {}) {
  if (value == null || value === "") return "—";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(getDateLocaleTag(), options);
}
